import { useContext, useEffect, useMemo, useState } from 'react'
import { CircularProgress } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Container from '@mui/material/Container'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import makeStyles from '@mui/styles/makeStyles'
import { bool, func, string, node, array, number, object, oneOfType } from 'prop-types'
import uuid from 'uuid/v4'
import { ApprovalContext } from '@templates/approval/contexts'
import { ZeroState } from './zero-state'

function desc(a, b, orderBy) {
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1
  }

  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1
  }

  if (typeof a[orderBy] === 'string') {
    if (b[orderBy].toLowerCase() < a[orderBy].toLowerCase()) {
      return -1
    } else if (b[orderBy].toLowerCase() > a[orderBy].toLowerCase()) {
      return 1
    }

    return 0
  }

  if (b[orderBy] < a[orderBy]) {
    return -1
  }

  if (b[orderBy] > a[orderBy]) {
    return 1
  }

  return 0
}

function getSorting(order, orderBy) {
  return order === 'DESC' || order === 'desc'
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy)
}

function EnhancedTableHead({
  multiSelect = false,
  columns = [],
  onSelectAllClick = () => {},
  order = '',
  orderBy = '',
  numSelected = 0,
  rowCount = 0,
  onRequestSort = () => {},
  headerRowPadding = 16,
}) {
  const createSortHandler = (property, index) => {
    onRequestSort(event, property.label, index)
  }

  const zIndexAboveCellRows = 4

  return (
    <TableHead style={{ position: 'relative', zIndex: zIndexAboveCellRows }}>
      <TableRow>
        {multiSelect && (
          <TableCell
            padding="checkbox"
            key="checkbox"
            style={{ borderTop: '1px solid #e8e8e8', backgroundColor: '#f4f4f4' }}
          >
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all desserts' }}
            />
          </TableCell>
        )}
        {columns.map((row, index) => (
          <TableCell
            key={`${index}-${row.label}`}
            align={row ? 'left' : 'right'}
            padding="normal"
            sortDirection={orderBy === row.lookup ? order : false}
            style={{
              width: row.width ? row.width : 'initial',
              paddingLeft: index === 0 ? headerRowPadding : 16,
              borderTop: '1px solid #e8e8e8',
              backgroundColor: '#f4f4f4',
            }}
          >
            <TableSortLabel
              active={orderBy === row.lookup}
              direction={order}
              onClick={() => !row.blockSort && createSortHandler(row, index)}
              hideSortIcon={row.blockSort}
              style={{
                pointerEvents: row.blockSort ? 'none' : 'all',
                fontWeight: 'bold',
                height: 'auto',
              }}
            >
              {row.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

EnhancedTableHead.propTypes = {
  numSelected: number.isRequired,
  onRequestSort: func.isRequired,
  onSelectAllClick: func.isRequired,
  order: string.isRequired,
  orderBy: string.isRequired,
  rowCount: number.isRequired,
  columns: array.isRequired,
  multiSelect: bool,
}

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: 2,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  tableWrapper: {
    overflow: 'auto',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflowX: 'auto',
  },
}))

export function CvsTable({
  children,
  emptyStateMessage,
  multiSelect,
  sortOrder: initialSortOrder,
  orderBy: initialOrderBy,
  pageSize,
  rows = [],
  noPagination,
  onSortedRowsChange,
  columns,
  selectAll,
  keyField,
  customHandleSort,
  isLoading = false,
  persistSortOptions,
  headerRowPadding = 16,
  overrideRootStyles = {},
  overrideTableWrapperStyles = {},
}) {
  const classes = useStyles()
  const [order, setOrder] = useState(initialSortOrder || 'desc')
  const [orderBy, setOrderBy] = useState(initialOrderBy)
  const [selected, setSelected] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(pageSize ? parseInt(pageSize) : 25)
  const [filteredRows, setFilteredRows] = useState([...rows])
  const approvalContext = useContext(ApprovalContext)

  useEffect(() => {
    setFilteredRows(rows)
    setSelected([])
  }, [rows])

  const sortedRows = useMemo(() => {
    let sorted = filteredRows.slice().sort(getSorting(order, orderBy))
    sorted = noPagination
      ? sorted
      : sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    onSortedRowsChange && onSortedRowsChange(sorted)
    return sorted || []
  }, [filteredRows, order, orderBy, page, rowsPerPage])

  function handleRequestSort(event, property, index) {
    if (persistSortOptions && approvalContext) {
      persistSortOptions(columns[index].lookup, order === 'desc' ? 'asc' : 'desc')
    }

    if (customHandleSort) {
      customHandleSort(columns[index].lookup)
    } else {
      setOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))
      setOrderBy(columns[index].lookup)
    }
  }

  function handleSelectAllClick(event) {
    selectAll(event.target.checked)
    if (event.target.checked) {
      const newSelecteds = rows.map(n => n[keyField])
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  function handleChangePage(event, newPage) {
    setPage(newPage)
  }

  function handleChangeRowsPerPage(event) {
    const newRowsPerPage = +event.target.value
    setRowsPerPage(newRowsPerPage)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center w-full h-full items-center">
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className={classes.root} data-test-cmp-id="enhanced-table" css={overrideRootStyles}>
      <TableContainer className={classes.tableWrapper} css={overrideTableWrapperStyles}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="medium"
          stickyHeader={noPagination}
        >
          <EnhancedTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={filteredRows.length}
            columns={columns}
            multiSelect={multiSelect}
            headerRowPadding={headerRowPadding}
          />
          <TableBody>
            {sortedRows.map((row, index) => {
              const Row = children
              return (
                <Row
                  row={row}
                  index={index}
                  key={uuid()}
                  style={{ paddingLeft: index === 0 ? 30 : 16 }}
                />
              )
            })}
          </TableBody>
        </Table>
        {filteredRows.length === 0 && (
          <Container>
            <div
              style={{
                padding: '20px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {emptyStateMessage && <ZeroState {...emptyStateMessage} />}
            </div>
          </Container>
        )}
      </TableContainer>
      {!noPagination ? (
        <TablePagination
          rowsPerPageOptions={[pageSize, pageSize * 2, pageSize * 4]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </div>
  )
}

CvsTable.propTypes = {
  children: oneOfType([node, func]),
  entity: string,
  emptyStateMessage: oneOfType([string, object]),
  multiSelect: bool,
  duplicateItem: func,
  deleteItems: func,
  sortOrder: string,
  orderBy: string,
  pageSize: number,
  rows: array,
  noPagination: bool,
  noAdd: bool,
  onSortedRowsChange: func,
  columns: array,
  selectAll: func,
  editItems: array,
  keyField: string,
  customHandleSort: func,
  persistSortOptions: func,
  currentPage: number,
  customPagination: bool,
}
